import { createHighlighter } from "shiki";
import parse, {
    domToReact,
    attributesToProps,
    htmlToDOM,
} from "html-react-parser";
import Image from "next/image";
import { decode } from "he";
import slugify from "slugify";
import React from "react";
import CopyLink from "@/components/Copy/CopyLink/CopyLink";
import CodeBlock from "@/components/Code/CodeBlock/CodeBlock";
import { GLOSSARY_DEMOS } from "@/components/content/GlossaryDemo/registry";
import { stripInvisibleChars } from "@/lib/invisibleChars";
import { sanitizeHtmlTree } from "@/helpers/sanitizeHtmlTree";

// Encadrés de contenu. TinyMCE les écrit en <blockquote class="warning">,
// mais un avertissement de l'auteur n'est pas une citation : au rendu, ils
// redeviennent des notes, et leur nom s'inscrit en vrai texte. L'icône de fond
// d'aujourd'hui ne le disait ni aux moteurs, ni aux lecteurs d'écran.
const CALLOUT_LABELS = {
    fr: {
        warning: "Attention",
        info: "Bon à savoir",
        question: "Question",
    },
    en: {
        warning: "Warning",
        info: "Good to know",
        question: "Question",
    },
};
const CALLOUT_KINDS = Object.keys(CALLOUT_LABELS.fr);

// Singleton Shiki highlighter — créé une seule fois, réutilisé partout
let highlighterPromise = null;

function getHighlighter() {
    if (!highlighterPromise) {
        if (!process.env.TINYMCE_CODESAMPLE_LANGUAGES) {
            throw new Error(
                "TINYMCE_CODESAMPLE_LANGUAGES n'est pas défini dans les variables d'environnement.",
            );
        }
        const values = process.env.TINYMCE_CODESAMPLE_LANGUAGES.map(
            (lang) => lang.value,
        );
        highlighterPromise = createHighlighter({
            themes: ["github-light", "github-dark"],
            langs: values,
        });
    }
    return highlighterPromise;
}

function extractText(nodes) {
    return nodes
        .map((node) => {
            if (node.type === "text") {
                return node.data;
            } else if (node.children) {
                return extractText(node.children);
            }
            return "";
        })
        .join("");
}

// Supprime les CSS custom properties (--*) des attributs style inline
// qui peuvent être copiées-collées depuis des pages Tailwind et crashent html-react-parser
function stripCssCustomProperties(str) {
    return str.replace(/style="([^"]*)"/g, (match, styleValue) => {
        const cleaned = styleValue
            .replace(/--[\w-]+\s*:\s*[^;]*;?\s*/g, "")
            .trim();
        if (!cleaned) return "";
        return `style="${cleaned}"`;
    });
}

// Les mêmes réglages d'analyse que `parse` : les deux chemins lisent le HTML
// de la même façon.
const PARSER_OPTIONS = { lowerCaseAttributeNames: false };

/**
 * `trusted` dit d'où vient le contenu. Par défaut il vient d'un membre
 * (discussion, message, correction) et il est nettoyé avant d'être rendu :
 * voir helpers/sanitizeHtmlTree.js. Un article, un terme du glossaire ou une
 * séance, écrits par l'équipe, passent `trusted: true` et gardent tout
 * (iframes, démos, styles). Sans l'option, un nouvel écran est donc protégé
 * d'office ; l'oublier sur un contenu de l'équipe lui retire ses vidéos
 * intégrées, pas sa sécurité.
 */
export async function transformHtml(content, unoptimized = false, { openLinksInNewTab = false, locale = "fr", trusted = false } = {}) {
    content = stripInvisibleChars(content);
    content = stripCssCustomProperties(content);

    const highlighter = await getHighlighter();

    const headings = [];
    const slugCounts = {};
    const codeBlocks = {};
    let codeBlockIndex = 0;

    try {
        // Highlight uniquement les blocs <pre><code>
        content = content.replace(
            /<pre\s+class="[^"]*\blanguage-([^"\s]+)\b[^"]*"[^>]*><code>(.*?)<\/code><\/pre>/gs,
            (match, lang, code) => {
                // Décodage spécifique du code dans les <pre><code>
                const decodedCode = decode(code);
                const highlightedCode = highlighter.codeToHtml(decodedCode, {
                    lang: lang === "markup" ? "html" : lang,
                    themes: {
                        light: "github-light",
                        dark: "github-dark",
                    },
                });

                const blockId = `code-block-${codeBlockIndex++}`;
                codeBlocks[blockId] = {
                    language: lang === "markup" ? "HTML" : lang.toUpperCase(),
                    code: decodedCode,
                    highlightedHtml: highlightedCode,
                };

                return `<div data-code-block-id="${blockId}"></div>`;
            },
        );
    } catch (error) {
        console.error(
            "Erreur lors de la mise en évidence des blocs de code :",
            error,
        );
    }

    // Décodage des iframes encodées
    // On cherche les balises du type &lt;iframe ... &gt; ... &lt;/iframe&gt; et on les transforme en <iframe ...> ... </iframe>
    // Réservé aux contenus de l'équipe : dans un message, une iframe tapée
    // en texte reste du texte.
    if (trusted) {
        content = content.replace(
            /&lt;iframe([\s\S]*?)&gt;&lt;\/iframe&gt;/gi,
            (match, attributes) => `<iframe${attributes}></iframe>`,
        );
    }

    // Vidéos YouTube collées dans l'éditeur : lecteur « sans cookie »
    // (youtube-nocookie.com), YouTube ne dépose rien tant que la vidéo n'est
    // pas lancée. Le contenu en base reste tel quel.
    content = content.replace(
        /(<iframe\b[^>]*?\bsrc=["'])(?:https?:)?\/\/(?:www\.)?youtube\.com\/embed\//gi,
        "$1https://www.youtube-nocookie.com/embed/",
    );

    const parserOptions = {
        replace: (domNode) => {
            // Transforme les code blocks en composant CodeBlock avec bouton copy
            if (
                domNode.name === "div" &&
                domNode.attribs?.["data-code-block-id"]
            ) {
                const blockId = domNode.attribs["data-code-block-id"];
                const block = codeBlocks[blockId];
                if (block) {
                    return (
                        <CodeBlock
                            key={blockId}
                            language={block.language}
                            code={block.code}
                            highlightedHtml={block.highlightedHtml}
                        />
                    );
                }
            }

            // Démos jouables insérées depuis le contenu : le HTML porte un
            // simple marqueur, le composant est résolu ici. Même mécanisme que
            // les blocs de code ci-dessus.
            if (
                domNode.name === "div" &&
                domNode.attribs?.["data-glossary-demo"]
            ) {
                const key = domNode.attribs["data-glossary-demo"];
                const Demo = GLOSSARY_DEMOS[key];

                if (Demo) {
                    return (
                        <Demo
                            key={`demo-${key}`}
                            lang={domNode.attribs["data-lang"] || "fr"}
                        />
                    );
                }

                // Marqueur inconnu : on ne rend rien plutôt qu'un bloc vide.
                return <></>;
            }

            // Emplacement d'image reste vide : le nettoyage a la validation
            // les supprime deja, ceci est le filet cote rendu. Un article
            // publie ne montre jamais un cadre en attente.
            if (
                domNode.name === "div" &&
                domNode.attribs?.["data-image-slot"] !== undefined
            ) {
                return <></>;
            }

            // Un tableau reçoit une enveloppe qui défile : trois colonnes
            // ne tiennent pas sur un téléphone, et sans elle le tableau
            // écrase son texte ou déborde de la page. Le dégradé de bord est
            // porté par le CSS, qui sait, lui, si le contenu dépasse.
            if (domNode.name === "table") {
                const props = attributesToProps(domNode.attribs || {});
                return (
                    <div className="table-scroll">
                        <table {...props}>{domToReact(domNode.children)}</table>
                    </div>
                );
            }

            // Un encadré devient une note, en gardant ses classes pour le
            // style. La citation nue, elle, reste un <blockquote> : c'en est
            // une vraie. `has-label` dit au CSS que le nom est déjà écrit, et
            // qu'il n'a pas à le dessiner lui-même comme dans l'éditeur.
            if (domNode.name === "blockquote" && domNode.attribs?.class) {
                const classNames = domNode.attribs.class.split(/\s+/);
                const kind = CALLOUT_KINDS.find((k) => classNames.includes(k));

                if (kind) {
                    const labels = CALLOUT_LABELS[locale] || CALLOUT_LABELS.fr;
                    return (
                        <div
                            className={`${domNode.attribs.class} has-label`}
                            role="note"
                        >
                            <span className="callout-label">
                                {labels[kind]}
                            </span>
                            {domToReact(domNode.children)}
                        </div>
                    );
                }
            }

            // Transforme les <img> en composant Image de Next.js
            if (domNode.name === "img") {
                let { src, alt } = domNode.attribs;
                const BASE_URL = "https://believemy.com";

                if (src && src.includes("../uploads")) {
                    src = `${BASE_URL}${src.replace(/^(\.\.\/)+/, "/")}`;
                }

                return (
                    <Image
                        src={src}
                        alt={alt || "Image"}
                        sizes="100vw"
                        width={0}
                        height={0}
                        className="w-full h-auto object-cover mb-3 rounded-lg"
                        unoptimized={unoptimized}
                        loading="lazy"
                        decoding="async"
                    />
                );
            }

            // Gérer les titres h2 à h5
            if (["h2", "h3", "h4", "h5"].includes(domNode.name)) {
                const level = parseInt(domNode.name.substring(1), 10);
                const text = extractText(domNode.children);
                let slug = slugify(text, { lower: true, strict: true });

                if (slugCounts[slug]) {
                    slugCounts[slug] += 1;
                    slug = `${slug}-${slugCounts[slug]}`;
                } else {
                    slugCounts[slug] = 1;
                }

                headings.push({ level, text, id: slug });

                return React.createElement(
                    domNode.name,
                    {
                        id: slug,
                        style: { scrollMarginTop: "80px" },
                        className: "group flex flex-wrap items-center",
                    },
                    [
                        domToReact(domNode.children),
                        <CopyLink key={`copy-${slug}`} id={slug} />,
                    ],
                );
            }

            // Ouvre les liens dans un nouvel onglet (formations uniquement)
            if (openLinksInNewTab && domNode.name === "a" && domNode.attribs?.href) {
                const props = attributesToProps(domNode.attribs, "a");
                return React.createElement(
                    "a",
                    {
                        ...props,
                        target: "_blank",
                        rel: "noopener noreferrer",
                    },
                    domToReact(domNode.children),
                );
            }

            // Les iframes décodées seront directement gérées comme des éléments du DOM.
            // Si vous souhaitez un traitement spécifique, vous pouvez l'ajouter ici.
        },
    };

    // Le contenu d'un membre est nettoyé ICI, une fois toutes les réécritures
    // faites et le HTML analysé : c'est l'arbre que React va rendre, plus rien
    // ne peut s'y glisser ensuite. Les marqueurs des blocs de code posés plus
    // haut traversent le nettoyage tels quels.
    const transformedContent = trusted
        ? parse(content, parserOptions)
        : content
          ? domToReact(
                sanitizeHtmlTree(htmlToDOM(content, PARSER_OPTIONS)),
                parserOptions,
            )
          : [];

    return { content: transformedContent, headings };
}
